import { MENU_TYPES, NEGOCE_TYPES, type LoadedProduct, type Store } from '@gozoki/api-types'
import { excludeFields, isNumber, shallowStringify } from '@gozoki/tools'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useReapproBoutiques } from '../hooks/stores.hooks'
import { resolveImages, zInputFile } from '../tools/images.tools'
import { frenchNumberSchema } from '../tools/zod-schemas'

export const zStoreProductMinimumStockForm = z.object({
  label: z.string().optional(),
  productReference: z.string(),
  storeReference: z.string(),
  weekEndMinimumStock: frenchNumberSchema.nullable(),
  mondayMinimumStock: frenchNumberSchema.nullable(),
  tuesdayMinimumStock: frenchNumberSchema.nullable(),
  wednesdayMinimumStock: frenchNumberSchema.nullable(),
  thursdayMinimumStock: frenchNumberSchema.nullable(),
  fridayMinimumStock: frenchNumberSchema.nullable(),
})

export const zProductForm = z.object({
  // Product ID
  reference: z
    .string()
    .nonempty()
    .regex(/^\d+$/, 'La référence doit contenir uniquement des chiffres')
    .length(13, 'La référence doit contenir exactement 13 chiffres'),
  dun14: z
    .string()
    .regex(/^\d+$/, 'Le code DUN 14 doit contenir uniquement des chiffres')
    .length(14, 'Le code DUN 14 doit contenir exactement 14 chiffres')
    .nullable(),
  label: z.string().nonempty(),

  // Product data
  weight: frenchNumberSchema.nullable(), // Grams
  price: frenchNumberSchema.nullable(), // Centimes
  weightPrice: frenchNumberSchema.nullable(), // Centimes per gram
  sellPrice: frenchNumberSchema.nullable(), // Centimes
  sellWeightPrice: frenchNumberSchema.nullable(), // Centimes per gram

  // This field is not used in the backend, but it helps control & persist the frontend state
  priceMode: z.string(), // Either 'fixed' or 'ratio' mode

  preparationMode: z.string(),
  ingredients: z.string(),

  // Nutritional values
  energyValue: z.string().refine(isNumber),
  fats: z.string().refine(isNumber),
  saturatedFats: z.string().refine(isNumber),
  carbohydrates: z.string().refine(isNumber),
  sugars: z.string().refine(isNumber),
  polyols: z.string().refine(isNumber),
  proteins: z.string().refine(isNumber),
  fibers: z.string().refine(isNumber),
  sodium: z.string().refine(isNumber),
  salt: z.string().refine(isNumber),

  allergens: z.string(),

  // Product de scription
  description: z.string().nonempty(),
  nutriscore: z.string().nullable(),
  conservation: z.string(),

  // Product status & relations
  isNew: z.coerce.boolean(), // Transform into boolean before submitting
  isActive: z.coerce.boolean(), // Transform into boolean before submitting
  brandId: z.number(),

  antiGaspi: frenchNumberSchema.nullable(), // Number before DLC to warn the user

  // Additional fields
  categories: z.array(z.number()),
  internalCategoryId: z.number(),
  reapproCategoryId: z.number(),

  // Attachments
  images: z.array(zInputFile), // Images to upload as product attachments

  previewImage: z.instanceof(File).nullable(),

  // Note : this field is used to persist the video url during upload / update process,
  // But it has no effect on the backend.
  preparationVideo: z.string().nullable(),
  colisage: frenchNumberSchema,
  colisWeight: frenchNumberSchema,
  menuType: z.enum(MENU_TYPES).nullable(),
  menuExtraPrice: frenchNumberSchema.nullable(),
  portions: z.string().nullable(),
  copilotCode: z.string().nullable(),
  negoceType: z.enum(NEGOCE_TYPES),
  storeProductMinimumStock: z.array(zStoreProductMinimumStockForm),

  // Anti-gaspi
  agPromo: frenchNumberSchema.nullable(),
  dlcDays: frenchNumberSchema.nullable(),
  franco: frenchNumberSchema.nullable(),
  coldStartStockMin: frenchNumberSchema.nullable(),

  establishmentId: z.number().nullable(),

  taxRate: frenchNumberSchema.nullable(),
})

export type ProductFormInput = z.input<typeof zProductForm>
export type ProductFormOutput = z.output<typeof zProductForm>

export const getDefaultStoreProductMinimumStock = (boutiques: Store[]) =>
  boutiques.map((store) => ({
    productReference: '',
    storeReference: store.checkpointStoreRef,
    weekEndMinimumStock: null,
    mondayMinimumStock: null,
    tuesdayMinimumStock: null,
    wednesdayMinimumStock: null,
    thursdayMinimumStock: null,
    fridayMinimumStock: null,
    label: store.label,
  }))

// Form hook
export const useProductForm = (defaultValues?: Partial<ProductFormInput>) => {
  const boutiques = useReapproBoutiques()

  const form = useForm({
    // Validation function
    defaultValues: {
      reference: '',
      dun14: null,
      label: '',
      weight: null,
      price: null,
      weightPrice: null,
      sellPrice: null,
      sellWeightPrice: null,
      description: '',
      nutriscore: null,
      conservation: '',
      brandId: 0,
      isNew: 'false',
      isActive: 'false',
      categories: [],
      images: [],
      priceMode: 'fixed',
      menuType: null,
      menuExtraPrice: '0',
      portions: '',
      copilotCode: '',
      allergens: '',
      colisWeight: '0',
      colisage: '0',
      preparationMode: '',
      ingredients: '',
      energyValue: '',
      fats: '',
      saturatedFats: '',
      carbohydrates: '',
      sugars: '',
      polyols: '',
      proteins: '',
      fibers: '',
      sodium: '',
      salt: '',
      internalCategoryId: 0,
      reapproCategoryId: 0,
      previewImage: null,
      preparationVideo: null,
      negoceType: 'NEGOCE INTERNE' as const,
      agPromo: '30',
      antiGaspi: '0',
      dlcDays: null,
      franco: null,
      coldStartStockMin: null,
      establishmentId: null,
      taxRate: '5.5',
      storeProductMinimumStock:
        defaultValues?.storeProductMinimumStock &&
        defaultValues?.storeProductMinimumStock.length > 0
          ? defaultValues.storeProductMinimumStock
          : (getDefaultStoreProductMinimumStock(boutiques.data ?? []) ?? []),
      ...defaultValues,
    },
    resolver: zodResolver(zProductForm),
  })

  return form
}

// Helper : generate precompleted form from existing product data for modification
export const fromExistingProduct = async (
  product: LoadedProduct,
  boutiques: Store[],
  storeProductMinimumStock?: z.infer<typeof zStoreProductMinimumStockForm>[],
): Promise<ProductFormOutput> =>
  shallowStringify({
    ...excludeFields(product, [
      'createdAt',
      'updatedAt',
      'id',
      'nutritionalValueId',
      'nutritionalValues',
      'images',
      'preparationVideo',
    ]), // Exclude autogenerated fields

    // Override the required values
    categories: product.categories.map((category) => category.id),
    internalCategoryId: product.internalCategory.id,
    reapproCategoryId: product.reapproCategory.id,

    // Unpack nutritional values
    energyValue: product.nutritionalValues.energyValue,
    fats: product.nutritionalValues.fats,
    carbohydrates: product.nutritionalValues.carbohydrates,
    proteins: product.nutritionalValues.proteins,
    fibers: product.nutritionalValues.fibers,
    sodium: product.nutritionalValues.sodium,
    salt: product.nutritionalValues.salt,
    saturatedFats: product.nutritionalValues.saturatedFats,
    sugars: product.nutritionalValues.sugars,
    polyols: product.nutritionalValues.polyols,

    // Other
    priceMode: product.weightPrice ? 'ratio' : 'fixed',

    images: await resolveImages(product.images),
    preparationVideo: product.preparationVideo?.url ?? null,
    previewImage: product.previewImage?.url ? new File([], product.previewImage.url) : null,
    copilotCode: product.copilotCode ?? '',
    preparationMode: product.preparationMode ?? '',
    colisage: product.colisage ?? '',
    colisWeight: product.colisWeight ?? 0,
    allergens: product.allergens ?? '',
    nutriscore: product.nutriscore ?? '',
    portions: product.portions ?? '',
    taxRate: product.taxRate ? Number(product.taxRate) : 5.5,

    storeProductMinimumStock: (() => {
      const allowedStoreRefs = new Set(boutiques.map((boutique) => boutique.checkpointStoreRef))
      const eligibleExistingStocks = (storeProductMinimumStock ?? []).filter((stock) =>
        allowedStoreRefs.has(stock.storeReference),
      )
      return eligibleExistingStocks.length > 0
        ? eligibleExistingStocks
        : (getDefaultStoreProductMinimumStock(boutiques) ?? [])
    })(),

    // Anti-gaspi
    agPromo: product.agPromo ?? 30,
    antiGaspi: product.antiGaspi ?? 0,
    dlcDays: product.dlcDays ?? 0,
    franco: product.franco ?? 0,
    coldStartStockMin: product.coldStartStockMin ?? 0,
    establishmentId: product.establishment?.id ?? null,
  })
